import { useState, useEffect } from "react";
import { Wallet, CreditCard, Smartphone, Banknote, Loader2 } from "lucide-react";
import axios from "axios";
import SectionCard from './sectioncard.tsx';
import Pill from './pill.tsx';
import { money } from "../../../utilities/currency.js";
import { fmtDate, estimateMinutes } from "../../../utilities/conversions.js";
import VippsAuthenticate from "../vippsauthenticate.tsx";
import StripeAuthenticate from "../stripeauthenticate.tsx";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import type { RootState } from "../../../state/store.ts"; 
type WalletSectionProps = {
    amountToTopup: number;
    baseUrl: string;
    currentUser: any;
}

export default function WalletSection({ amountToTopup, baseUrl, currentUser }: WalletSectionProps) {

    const client = useSelector((state: RootState) => state.client.client);
    const client_uid = client.uid || "clnt_QI9Jd1m0"; // fallback to default if not available

    const [vippsAuthenticate, setVippsAuthenticate] = useState(false);
    const [stripeAuthenticate, setStripeAuthenticate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(299); // NOK
    const [method, setMethod] = useState<'vipps' | 'card'>("vipps");
    const [preset, setPreset] = useState<number | null>(399);
    const [custom, setCustom] = useState<number | ''>('');
    const [topupLoading, setTopupLoading] = useState(false);
    const [txs, setTxs] = useState<Array<{ id: string, dated: string, amount: number, payment_method: string }>>([
        { id: 't1', dated: new Date(Date.now() - 86400000 * 3).toISOString(), amount: 199, payment_method: 'Vipps' },
        { id: 't2', dated: new Date(Date.now() - 86400000 * 7).toISOString(), amount: 399, payment_method: 'Card' },
    ]);

    const [customer, setCustomer] = useState<any>(null);
    useEffect(() => {
        axios.get(baseUrl + '/getCustomerByUID', { params: { token: currentUser.token } }).then(res => {
            setCustomer(res.data.data);
        }).catch(err => {
            console.error("Error fetching customer data:", err);
        });
    },[currentUser?.token, baseUrl]);

    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.data?.type === "WALLET_TOPUP") {
                Swal.fire({
                    icon: "success",
                    title: "Betaling godkjent",
                    text: "Takk for at betalingen din på " + event.data.amount + " er fullført.",
                    showConfirmButton: true,
                });
            }
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    });

    const topupPresets = [149, 399, 999];
    function getWalletHistory() {
        setTopupLoading(true);
        axios.get(baseUrl + '/getWalletHistory', { params: { token: currentUser.token } }).then(res => {
            // console.log(res.data.data);
            setLoading(false);
            setTxs(res.data.data);
        }).catch(err => {
            console.error("Error fetching wallet data:", err);
        }).finally(() => {
            setTopupLoading(false);
        });
    }
    async function doTopup() {
        if (!amountToTopup || amountToTopup < 10) return;
        setTopupLoading(true);
        var ep = "";
        if (method === "vipps") {
            setVippsAuthenticate(true);
            ep = baseUrl + "/buyWalletCreditVipps";
        } else if (method === "card") {
            setStripeAuthenticate(true);
            ep = baseUrl + "/buyWalletCreditVipps";
        }
        
        var formData = new FormData();
        formData.append("amount", custom? custom?.toString() || '0' : amountToTopup.toString());
        formData.append("paymentMethod", method);
        formData.append("client_uid", client_uid);
        formData.append("phone", customer.phone);
        formData.append("country", customer.country_code);
        const res = await axios.post(ep, formData);

        // console.log(res.data);

        if (method === "vipps" && res.data.status === "OK") {
            const { status, reference, access_token, endpoint, customer_uid } = res.data;
            var formData1 = new FormData();
            formData1.append("customer_uid", customer_uid);
            formData1.append("access_token", access_token);
            formData1.append("reference", reference);
            formData1.append("endpoint", endpoint);
            formData1.append("client_uid", client_uid);

            const pollAuth = async (intervalId: ReturnType<typeof setInterval>) => {
                try {
                    const secondRes = await axios.post(baseUrl + "/authenticateBuyWalletCreditVipps", formData1);

                    // console.log("Polling result:", secondRes.data);

                    if (secondRes.data.status && secondRes.data.status === "OK") {

                        setVippsAuthenticate(false);
                        clearInterval(intervalId); // ✅ stop polling
                        Swal.fire({
                            icon: "success",
                            title: "Betaling godkjent",
                            text: "Takk for at betalingen din på " + (custom ? custom?.toString() || '0' : amountToTopup) + " er fullført.",
                            showConfirmButton: true,
                        });
                        // setSuccessMsg(t.success(amount) || "Payment authorized!");
                    }else if(secondRes.data.status && secondRes.data.status === "ABORTED"){
                        setVippsAuthenticate(false);
                        clearInterval(intervalId); // ✅ stop polling
                        Swal.fire({
                            icon: "error",
                            title: "Betaling avbrutt",
                            text: "Du har avbrutt betalingen.",
                            showConfirmButton: true,
                        });
                    }
                } catch (err) {
                    clearInterval(intervalId); // stop polling on error
                }
            };

            // Start polling every 2s
            const intervalId = setInterval(() => pollAuth(intervalId), 2000);
        } else if (method === "card") {
            setStripeAuthenticate(false);
            window.open(res.data.url);
        }
        setTimeout(() => {
            setTopupLoading(false);
            setPreset(399);
            setCustom('');
        }, 1000);
    }
    useEffect(() => {
        getWalletHistory();
    }, [currentUser?.token, baseUrl]);
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-600 animate-pulse">Laster inn profil…</p>
                </div>
            </div>
        );
    }
    if (vippsAuthenticate) {
        return <VippsAuthenticate vippsAuthenticate={vippsAuthenticate} />
    }
    if (stripeAuthenticate) {
        return <StripeAuthenticate stripeAuthenticate={stripeAuthenticate} />
    }
    return (
        <SectionCard
            title="Lommebok – ringeminutter"
            icon={<Wallet className="h-5 w-5 text-purple-700" />}
            actions={<Pill tone="blue"><Banknote className="h-3 w-3" /> Saldo: {money(balance)} • ca {estimateMinutes(balance)} min</Pill>}
        >
            <div className="grid gap-4 md:grid-cols-3">
                {/* Påfyll */}
                <div className="md:col-span-2">
                    <div className="rounded-xl border p-4">
                        <div className="text-sm font-semibold text-purple-900 mb-2">Fyll på saldo</div>
                        <div className="flex flex-wrap gap-2">
                            {topupPresets.map((p) => (
                                <button key={p} onClick={() => { setPreset(p); setCustom(''); }} className={`rounded-full px-4 py-2 border ${preset === p ? 'bg-purple-600 text-white border-purple-600' : 'border-purple-300 text-purple-800 hover:border-purple-500'}`}>{money(p)}</button>
                            ))}
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 ml-1">Egendefinert</span>
                                <input value={custom} onChange={(e) => { setPreset(null); const v = e.target.value.replace(/[^0-9]/g, ''); setCustom(v ? Number(v) : ''); }} className="w-28 rounded-xl border px-3 py-2" placeholder="NOK" />
                            </div>
                        </div>
                        <div className="mt-3 text-sm text-gray-600">{money(amountToTopup)} → ca {estimateMinutes(amountToTopup)} min</div>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <label className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 cursor-pointer ${method === 'vipps' ? 'border-purple-600' : ''}`}>
                                <input type="radio" name="method" className="hidden" checked={method === 'vipps'} onChange={() => setMethod('vipps')} />
                                <Smartphone className="h-4 w-4" /> Vipps
                            </label>
                            <label className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 cursor-pointer ${method === 'card' ? 'border-purple-600' : ''}`}>
                                <input type="radio" name="method" className="hidden" checked={method === 'card'} onChange={() => setMethod('card')} />
                                <CreditCard className="h-4 w-4" /> Card
                            </label>
                            <button onClick={doTopup} disabled={topupLoading || amountToTopup < 10} className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-5 py-2 text-white font-semibold hover:bg-purple-700 disabled:opacity-50">
                                {topupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
                                Fyll på nå
                            </button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">Ingen innlogging nødvendig for Vipps. For kort må kortet være registrert på ditt telefonnummer.</p>
                    </div>
                </div>

                {/* Tx-logg */}
                <div>
                    <div className="rounded-xl border p-4">
                        <div className="text-sm font-semibold text-purple-900 mb-2">Transaksjoner</div>
                        <ul className="space-y-2 max-h-64 overflow-auto pr-1">
                            {txs.map(tx => (
                                <li key={tx.id} className="flex items-center justify-between rounded-lg border p-2">
                                    <div>
                                        <div className="text-sm">{fmtDate(tx.dated)}</div>
                                        <div className="text-xs text-gray-500">{tx.payment_method}</div>
                                    </div>
                                    <div className="font-semibold">+{money(tx.amount)}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </SectionCard>
    );
}